'use client';

import { IconChevronRightFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-right-filled';
import { interactionResponse } from '@zillow/yield-callback';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { photoCarouselNavButtonRecipe } from './styles/photo-carousel-nav-button.styles';
import { photoCarouselNextButtonStyles } from './styles/photo-carousel-next-button.styles';
import { handOffFocusToSibling } from './utils/hand-off-focus-to-sibling';
import type { PhotoCarouselNextButtonPropsInterface } from './types/photo-carousel-next-button.types';

/**
 * Control used to show the next photo in the carousel.
 *
 * Along with `PhotoCarousel.PreviousButton`, this is the keyboard interface for the
 * carousel — the dots are decorative [CDS-5858].
 *
 * @related ['IconButton']
 * @since 10.0.0
 */

export const PhotoCarouselNextButton = forwardRef<
  HTMLButtonElement,
  PhotoCarouselNextButtonPropsInterface
>((props: PhotoCarouselNextButtonPropsInterface, forwardedRef) => {
  const {
    activeIndex = 0,
    shouldAwaitInteractionResponse,
    shouldLoop = true,
    totalSlides = 0,
    updateActiveIndex,
  } = useContext(PhotoCarouselRootContext);
  const {
    children = (
      <Icon>
        <IconChevronRightFilled />
      </Icon>
    ),
    css: cssProp,
    disabled = !shouldLoop && activeIndex === totalSlides - 1,
    onClick,
    ...rest
  } = props;

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      // Stop propagation immediately to prevent invoking Property Card's `onClick`
      event.stopPropagation();

      if (disabled) return;

      // Captured before the `await` below, after which React has reset `currentTarget`.
      const button = event.currentTarget;

      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      const nextIndex = activeIndex + 1;

      updateActiveIndex?.(nextIndex);

      if (!shouldLoop && nextIndex === totalSlides - 1) {
        handOffFocusToSibling(button, 'c11n_photo-carousel__previous-button');
      }

      onClick?.(event);
    },
    [
      activeIndex,
      disabled,
      onClick,
      shouldAwaitInteractionResponse,
      shouldLoop,
      totalSlides,
      updateActiveIndex,
    ],
  );

  return (
    <IconButton
      data-c11n-component="PhotoCarousel.NextButton"
      disabled={disabled}
      emphasis="bare"
      onClick={handleClick}
      onImpact
      ref={forwardedRef}
      title="Next photo"
      tone="neutral"
      css={css.raw(
        photoCarouselNavButtonRecipe.raw({ disabled }),
        photoCarouselNextButtonStyles,
        cssProp,
      )}
      {...rest}
      className={cx('c11n_photo-carousel__next-button', rest.className)}
    >
      {children}
    </IconButton>
  );
});

PhotoCarouselNextButton.displayName = 'PhotoCarousel.NextButton';
